// Lifted from web + mobile MaxesContext (ALIGNMENT_AUDIT HIGH 1).
// Both surfaces deserialised `/api/me/maxes` with the exact same loop
// (Max.safeParse → Map<exerciseId, valueKg>); a divergence here would
// produce silently different MAX maps on web vs mobile, so the read is
// canonical and lives in `@jackedlogs/shared`.

import type { ApiGet } from "./apiFetch.js";
import { Max } from "./schemas/max.js";
import type { CurrentMax } from "./maxRegression.js";

/**
 * Minimal contract the surface-side `apiFetch` must honour. Web and
 * mobile each add their own option bags (`ApiOptions`, retry policy,
 * Idempotency-Key); none of that matters for a plain GET, so the lifted
 * helper only requires a path-in / parsed-JSON-out shape.
 */
export type ApiFetcher = ApiGet;

// CO-11 coach read-through: an optional athleteSub routes the GET at an
// assigned athlete's MAXes (edge-gated server-side). Self reads pass none.
const maxesPath = (athleteSub?: string): string =>
  athleteSub
    ? `/api/me/maxes?athleteSub=${encodeURIComponent(athleteSub)}`
    : "/api/me/maxes";

/**
 * Fetches `/api/me/maxes` and deserialises into a `Map<exerciseId, kg>`.
 *
 * Skips rows that fail `Max.safeParse` — matches the previous in-line
 * web + mobile behaviour: a single bogus row in the response shouldn't
 * blow up the entire MAX map. Storage stays canonical kg; the consumer
 * formats for display.
 */
export const fetchMaxesMap = async (
  apiFetch: ApiFetcher,
  athleteSub?: string,
): Promise<Map<string, number>> => {
  const data = await apiFetch<unknown>(maxesPath(athleteSub));
  const arr = Array.isArray(data) ? data : [];
  const next = new Map<string, number>();
  for (const raw of arr) {
    const p = Max.safeParse(raw);
    if (p.success) next.set(p.data.exerciseId, p.data.valueKg);
  }
  return next;
};

/**
 * Fetches `/api/me/maxes` and deserialises into a `Map<exerciseId,
 * {valueKg, setAt}>`. Twin of `fetchMaxesMap` that KEEPS the `setAt`
 * timestamp — the bidirectional MAX-regression check
 * (detectMaxRegression) needs it for the staleness gate, while the
 * legacy callers only want kg. Same row-skip-on-parse-failure behaviour.
 * The kg-only map can be derived from this (`new Map([...m].map(([k, v])
 * => [k, v.valueKg]))`) so a surface that needs both fetches once.
 */
export const fetchMaxesDetailed = async (
  apiFetch: ApiFetcher,
  athleteSub?: string,
): Promise<Map<string, CurrentMax>> => {
  const data = await apiFetch<unknown>(maxesPath(athleteSub));
  const arr = Array.isArray(data) ? data : [];
  const next = new Map<string, CurrentMax>();
  for (const raw of arr) {
    const p = Max.safeParse(raw);
    if (p.success) {
      next.set(p.data.exerciseId, {
        valueKg: p.data.valueKg,
        setAt: p.data.setAt,
        // Carried, not dropped: a reader that PROPOSES a load off this value
        // has to know whether it was tested or merely seeded at import
        // (isMaxTrustedForProposal). The regression check ignores it.
        source: p.data.source,
      });
    }
  }
  return next;
};
